import React, { useState, useEffect } from "react";
import { X, Cog, ChevronDown, ChevronUp } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";

const AssignOperatorDialog = ({ batch, order, lineItem, onClose, onSuccess }) => {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assignmentMode, setAssignmentMode] = useState("single"); // 'single' or 'all'
  const [selectedMachine, setSelectedMachine] = useState("");
  const [expandedStage, setExpandedStage] = useState(null);
  
  // For bulk assignment (all stages with different machines)
  const [stageAssignments, setStageAssignments] = useState({});

  const currentStage = batch.current_stage_name;
  const currentStageIndex = batch.current_stage_index || 0;
  const allStages = lineItem.production_stages || [];
  const pendingStages = allStages.slice(currentStageIndex);

  useEffect(() => {
    if (assignmentMode === "single") {
      fetchMachinesForStage(currentStage);
    }
  }, [currentStage, assignmentMode]);

  const fetchMachinesForStage = async (stageName) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/production/operator-assignments/stages/${encodeURIComponent(stageName)}/operators`);
      setMachines(data.machines || []);
      // Auto-select if only one machine
      if ((data.machines || []).length === 1) {
        setSelectedMachine(data.machines[0].id);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedMachine && assignmentMode === "single") {
      toast.error("Pick a machine first");
      return;
    }

    try {
      setLoading(true);

      if (assignmentMode === "single") {
        await api.post(`/production/operator-assignments/batches/${batch.id}/assign`, {
          stage_name: currentStage,
          machine_id: selectedMachine,
        });
        onSuccess?.();
        onClose();
      } else {
        const assignments = Object.entries(stageAssignments)
          .filter(([_, data]) => data.machine_id)
          .map(([stageName, data]) => ({
            stage_name: stageName,
            machine_id: data.machine_id,
          }));

        if (assignments.length === 0) {
          toast.error("Assign a machine to at least one stage");
          return;
        }

        await api.post(`/production/operator-assignments/batches/${batch.id}/bulk-assign`, { assignments });
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign machine");
    } finally {
      setLoading(false);
    }
  };

  const handleStageExpand = async (stageName) => {
    if (expandedStage === stageName) {
      setExpandedStage(null);
    } else {
      setExpandedStage(stageName);
      if (!stageAssignments[stageName]?.machinesLoaded) {
        await fetchMachinesForStageInBulk(stageName);
      }
    }
  };

  const fetchMachinesForStageInBulk = async (stageName) => {
    try {
      const { data } = await api.get(`/production/operator-assignments/stages/${encodeURIComponent(stageName)}/operators`);
      setStageAssignments((prev) => ({
        ...prev,
        [stageName]: {
          ...prev[stageName],
          machines: data.machines || [],
          machinesLoaded: true,
        },
      }));
    } catch (error) {
    }
  };

  const updateStageAssignment = (stageName, field, value) => {
    setStageAssignments((prev) => ({
      ...prev,
      [stageName]: {
        ...prev[stageName],
        [field]: value,
      },
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Assign Machine
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {order.order_id} - Batch #{batch.batch_number} ({batch.quantity}{" "}
              pcs)
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Assignment Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assignment Mode
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setAssignmentMode("single")}
                className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${
                  assignmentMode === "single"
                    ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <span className="font-medium">Current Stage</span>
                <span className="block text-xs text-gray-500 mt-1">
                  Assign for {currentStage} only
                </span>
              </button>
              <button
                onClick={() => setAssignmentMode("all")}
                className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${
                  assignmentMode === "all"
                    ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <span className="font-medium">All Pending Stages</span>
                <span className="block text-xs text-gray-500 mt-1">
                  Assign upfront ({pendingStages.length} stages)
                </span>
              </button>
            </div>
          </div>

          {/* Single Stage Assignment */}
          {assignmentMode === "single" && (
            <div className="space-y-4">
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                <p className="text-sm font-medium text-indigo-900">
                  Current Stage: <span className="font-bold">{currentStage}</span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Machine *
                </label>
                <Select value={selectedMachine} onValueChange={v => setSelectedMachine(v)} disabled={loading}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Choose machine..." />
                  </SelectTrigger>
                  <SelectContent>
                    {machines.map((machine) => (
                      <SelectItem key={machine.id} value={machine.id}>
                        <span className="flex items-center gap-2">
                          <Cog className="h-3 w-3" />
                          {machine.machine_name}
                          {machine.machine_code && ` (${machine.machine_code})`}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {machines.length === 0 && !loading && (
                  <p className="text-xs text-amber-600 mt-1">
                    No machines linked to this stage. Ask admin to configure machine-stage links.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Bulk Assignment (All Stages) */}
          {assignmentMode === "all" && (
            <div className="space-y-2">
              <p className="text-sm text-gray-600 mb-3">
                Assign machines for all pending stages. Click each stage to expand and select a machine.
              </p>
              {pendingStages.map((stage, idx) => {
                const stageName = stage.stage_name;
                const isExpanded = expandedStage === stageName;
                const stageData = stageAssignments[stageName] || {};

                return (
                  <div
                    key={idx}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    <button
                      onClick={() => handleStageExpand(stageName)}
                      className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex justify-between items-center transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Cog className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">
                          {stageName}
                        </span>
                        {stageData.machine_id && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                            Assigned
                          </span>
                        )}
                      </div>
                      {isExpanded ? (
                        <ChevronUp size={20} className="text-gray-400" />
                      ) : (
                        <ChevronDown size={20} className="text-gray-400" />
                      )}
                    </button>

                    {isExpanded && (
                      <div className="p-4 space-y-3 bg-white">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Machine
                          </label>
                          <Select
                            value={stageData.machine_id || ""}
                            onValueChange={v => updateStageAssignment(stageName, "machine_id", v === '__none__' ? '' : v)}
                          >
                            <SelectTrigger className="w-full text-sm">
                              <SelectValue placeholder="Choose machine..." />
                            </SelectTrigger>
                            <SelectContent>
                              {(stageData.machines || []).map((machine) => (
                                <SelectItem key={machine.id} value={machine.id}>
                                  {machine.machine_name}
                                  {machine.machine_code && ` (${machine.machine_code})`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleAssign}
              disabled={loading}
              className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {loading ? "Assigning..." : "Assign Machine"}
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignOperatorDialog;
